import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import StudentFormDialog from "../components/StudentFormDialog";
import ImportStudentsDialog from "../components/ImportStudentsDialog";
import { Plus, Pencil, Trash2, Users, UserPlus, FileUp } from "lucide-react";

export default function Groups() {
  const { t } = useLang();
  const [groups, setGroups] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [addStudentGroup, setAddStudentGroup] = useState(null);
  const [importGroup, setImportGroup] = useState(null);

  const load = useCallback(async () => {
    const [g, s] = await Promise.all([api.get("/groups"), api.get("/students")]);
    setGroups(g.data);
    const c = {};
    s.data.forEach((st) => { c[st.group_id] = (c[st.group_id] || 0) + 1; });
    setCounts(c);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setName(""); setError(""); setOpen(true); };
  const openEdit = (g) => { setEditing(g); setName(g.name); setError(""); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      if (editing) {
        await api.put(`/groups/${editing.id}`, { name });
        toast.success(t("groups.renamed"));
      } else {
        await api.post("/groups", { name });
        toast.success(t("groups.created"));
      }
      setOpen(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(t("groups.confirmDelete"))) return;
    try {
      await api.delete(`/groups/${g.id}`);
      toast.success(t("groups.deleted"));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground">{t("groups.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("groups.subtitle")}</p>
        </div>
        <button onClick={openNew} data-testid="add-group-btn" className="brand-btn inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90">
          <Plus className="h-4 w-4" /> {t("groups.add")}
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="surface-card rounded-xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
          {t("groups.empty")}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div key={g.id} className="surface-card rounded-xl border border-border bg-card p-5" data-testid={`group-card-${g.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg text-white" style={{ backgroundColor: "var(--brand)" }}>
                    <Users className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-foreground">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{counts[g.id] || 0} {t("groups.students")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setAddStudentGroup(g)} data-testid={`add-student-group-${g.id}`} aria-label={t("attendance.addStudent")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary">
                    <UserPlus className="h-4 w-4" />
                  </button>
                  <button onClick={() => setImportGroup(g)} data-testid={`import-students-group-${g.id}`} aria-label={t("importStudents.button")} title={t("importStudents.button")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary">
                    <FileUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => openEdit(g)} data-testid={`rename-group-${g.id}`} aria-label={t("groups.rename")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(g)} data-testid={`delete-group-${g.id}`} aria-label={t("groups.delete")}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("groups.editTitle") : t("groups.newTitle")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("groups.name")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("groups.namePh")} data-testid="group-name-input" required autoFocus />
            </div>
            {error && <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="group-error">{error}</div>}
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-secondary">{t("groups.cancel")}</button>
              <button type="submit" disabled={saving} data-testid="group-save" className="brand-btn rounded-lg px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60">{t("groups.save")}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StudentFormDialog open={!!addStudentGroup} onOpenChange={(v) => !v && setAddStudentGroup(null)} groups={groups}
        student={null} presetGroupId={addStudentGroup?.id} onSaved={() => { setAddStudentGroup(null); load(); }} />
      <ImportStudentsDialog group={importGroup} onOpenChange={(v) => !v && setImportGroup(null)} onDone={load} />
    </div>
  );
}
